import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { planFromPriceId, planFromProductId, priceIdFor } from "./stripe-prices";
import type { PlanId } from "./plans";
import { isLiveHostname, requestHostname } from "./environment";

const CheckoutInput = z.object({
  plan: z.enum(["plus", "pro", "expert"]),
  interval: z.enum(["month", "year"]).default("month"),
  returnOrigin: z.string().url().optional(),
});

const PortalInput = z.object({
  returnOrigin: z.string().url().optional(),
});

function validOrigin(value: string | null): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    const allowed =
      url.hostname === "sellavo.com" ||
      url.hostname === "www.sellavo.com" ||
      url.hostname === "localhost" ||
      url.hostname.endsWith(".lovable.app") ||
      url.hostname.endsWith(".lovableproject.com");
    if (!allowed) return null;
    return url.origin;
  } catch {
    return null;
  }
}

function originFromRequest(requestedOrigin?: string): string {
  const explicitOrigin = validOrigin(requestedOrigin ?? null);
  if (explicitOrigin) return explicitOrigin;

  const req = getRequest();
  // Serverfunktioner proxas internt och `host` kan därför vara localhost.
  // Browserns Origin är den adress kunden faktiskt använder och ska återvända till.
  const browserOrigin = validOrigin(req?.headers.get("origin"));
  if (browserOrigin) return browserOrigin;

  const referer = validOrigin(req?.headers.get("referer"));
  if (referer) return referer;

  const proto = req?.headers.get("x-forwarded-proto") ?? "https";
  const host = req?.headers.get("x-forwarded-host") ?? req?.headers.get("host");
  const forwardedOrigin = host ? validOrigin(`${proto}://${host}`) : null;
  if (forwardedOrigin && !forwardedOrigin.includes("localhost")) return forwardedOrigin;

  return "https://sellavo.com";
}

/** Hittar (eller skapar) kunden i Stripe för den inloggade användaren. */
async function ensureCustomer(userId: string, email: string | null): Promise<string> {
  const { stripeApi } = await import("./stripe.server");
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const db = supabaseAdmin as any;

  const { data: row } = await db
    .from("user_plans")
    .select("stripe_customer_id")
    .eq("user_id", userId)
    .maybeSingle();

  if (row?.stripe_customer_id) {
    const existing = await stripeApi<any>(`/customers/${row.stripe_customer_id}`).catch(() => null);
    if (existing && !existing.deleted) return existing.id as string;
  }

  const search = await stripeApi<any>("/customers/search", {
    body: { query: `metadata['user_id']:'${userId}'`, limit: 1 },
  }).catch(() => null);
  const found = search?.data?.[0]?.id as string | undefined;

  const customerId =
    found ??
    ((
      await stripeApi<any>("/customers", {
        method: "POST",
        body: { email: email ?? undefined, metadata: { user_id: userId } },
      })
    ).id as string);

  await db
    .from("user_plans")
    .upsert({ user_id: userId, stripe_customer_id: customerId }, { onConflict: "user_id" });

  return customerId;
}

/** Startar en betalning i Stripe Checkout och returnerar adressen dit. */
export const startCheckout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => CheckoutInput.parse(input))
  .handler(async ({ data, context }) => {
    const request = getRequest();
    const originHost = data.returnOrigin ? new URL(data.returnOrigin).hostname : requestHostname(request);
    if (data.plan === "expert" && isLiveHostname(originHost)) {
      throw new Error("Expert går inte att köpa på livesajten just nu.");
    }
    const { stripeApi } = await import("./stripe.server");
    const priceId = priceIdFor(data.plan as PlanId, data.interval);
    if (!priceId) throw new Error("Nivån går inte att köpa just nu.");

    const email = (context.claims as any)?.email ?? null;
    const customer = await ensureCustomer(context.userId, email);
    const origin = originFromRequest(data.returnOrigin);

    const session = await stripeApi<any>("/checkout/sessions", {
      method: "POST",
      body: {
        mode: "subscription",
        customer,
        line_items: [{ price: priceId, quantity: 1 }],
        allow_promotion_codes: true,
        client_reference_id: context.userId,
        subscription_data: { metadata: { user_id: context.userId, plan: data.plan } },
        success_url: `${origin}/min-niva?betalning=klar`,
        cancel_url: `${origin}/priser?betalning=avbruten`,
      },
    });

    return { url: session.url as string };
  });

/** Öppnar Stripes kundportal där man byter kort eller säger upp. */
export const openBillingPortal = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => PortalInput.parse(input))
  .handler(async ({ data, context }) => {
    const { stripeApi } = await import("./stripe.server");
    const email = (context.claims as any)?.email ?? null;
    const customer = await ensureCustomer(context.userId, email);
    const origin = originFromRequest(data.returnOrigin);

    const session = await stripeApi<any>("/billing_portal/sessions", {
      method: "POST",
      body: { customer, return_url: `${origin}/min-niva` },
    });

    return { url: session.url as string };
  });

/**
 * Läser av abonnemanget hos Stripe och skriver in nivån på kontot.
 * Anropas när man kommer tillbaka från betalningen och när nivåsidan öppnas.
 */
export const syncMySubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { stripeApi } = await import("./stripe.server");
    const email = (context.claims as any)?.email ?? null;
    const customer = await ensureCustomer(context.userId, email);

    const subs = await stripeApi<any>("/subscriptions", {
      body: { customer, status: "all", limit: 10, "expand[]": "data.items.data.price" },
    });

    const active = (subs.data ?? []).find((s: any) =>
      ["active", "trialing", "past_due"].includes(s.status),
    );

    if (!active) {
      const admin = (await import("@/integrations/supabase/client.server")).supabaseAdmin as any;
      // En betald nivå som inte är kopplad till ett Stripe-abonnemang är
      // tilldelad av admin (eller köpt utanför Stripe) – den får aldrig
      // skrivas över till Gratis bara för att Stripe saknar abonnemang.
      const { data: existing } = await admin
        .from("user_plans")
        .select("plan, stripe_subscription_id, current_period_end")
        .eq("user_id", context.userId)
        .maybeSingle();
      if (existing && existing.plan !== "free" && !existing.stripe_subscription_id) {
        await admin
          .from("user_plans")
          .update({ stripe_customer_id: customer })
          .eq("user_id", context.userId);
        return {
          plan: existing.plan as PlanId,
          subscribed: false,
          currentPeriodEnd: existing.current_period_end ?? null,
        };
      }
      await admin.from("user_plans").upsert(
        {
          user_id: context.userId,
          plan: "free",
          stripe_customer_id: customer,
          stripe_subscription_id: null,
          billing_interval: null,
          cancel_at_period_end: false,
          current_period_end: null,
        },
        { onConflict: "user_id" },
      );
      return { plan: "free" as PlanId, subscribed: false, currentPeriodEnd: null };
    }

    const item = active.items?.data?.[0];
    const price = item?.price;
    const mapped = price?.id ? planFromPriceId(price.id) : null;
    const plan: PlanId =
      mapped?.plan ??
      planFromProductId(typeof price?.product === "string" ? price.product : "") ??
      "plus";
    const interval: string | null = mapped?.interval ?? price?.recurring?.interval ?? null;

    const periodEndUnix: number | null =
      active.current_period_end ?? item?.current_period_end ?? null;
    const periodStartUnix: number | null =
      active.current_period_start ?? item?.current_period_start ?? null;
    const currentPeriodEnd = periodEndUnix ? new Date(periodEndUnix * 1000).toISOString() : null;
    const currentPeriodStart = periodStartUnix
      ? new Date(periodStartUnix * 1000).toISOString()
      : null;

    await ((await import("@/integrations/supabase/client.server")).supabaseAdmin as any).from("user_plans").upsert(
      {
        user_id: context.userId,
        plan,
        stripe_customer_id: customer,
        stripe_subscription_id: active.id,
        billing_interval: interval,
        cancel_at_period_end: active.cancel_at_period_end === true,
        current_period_end: currentPeriodEnd,
        current_period_start: currentPeriodStart,
      },
      { onConflict: "user_id" },
    );

    return {
      plan,
      subscribed: true,
      interval,
      cancelAtPeriodEnd: active.cancel_at_period_end === true,
      currentPeriodEnd,
    };
  });

/** Talar om ifall betalningarna körs i testläge (bara för inloggade). */
export const getBillingMode = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async () => {
    const { stripeIsTestMode } = await import("./stripe.server");
    return { testMode: stripeIsTestMode() };
  });
